import { Component } from '@theme/component';

const TEASER_HIDDEN_KEY = 'despina-popup-teaser-hidden';
const SUBSCRIBED_KEY = 'despina-popup-subscribed';
const AUTO_OPENED_KEY = 'despina-popup-auto-opened';
const TEASER_HIDE_DAYS = 7;
const TEASER_DELAY_MS = 1200;

/** Storage can be unavailable (private mode, blocked site data): treat it as empty */
const storage = {
  /** @param {string} key */
  get(key) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  /** @param {string} key @param {string} value */
  set(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Not persisting is fine
    }
  },
};

/**
 * Newsletter pop-up: a corner tab that opens a modal <dialog> with the sign-up form. The form posts
 * normally (Shopify customer form); when the page comes back with this form's result, the dialog
 * reopens to show the thank-you (or the error). The tab stays hidden for a week after it is closed
 * and for good once the visitor has signed up.
 *
 * @typedef {object} NewsletterPopupRefs
 * @property {HTMLElement} [teaser] - The bottom-left tab
 * @property {HTMLDialogElement} [dialog] - The pop-up
 * @property {HTMLElement} [state] - Carries the form result ('success' | 'error' | '')
 * @property {HTMLElement} [code] - The discount code text
 *
 * @extends {Component<NewsletterPopupRefs>}
 */
class NewsletterPopupComponent extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[]} */
  #timers = [];

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const { dialog, state } = this.refs;

    const result = state?.dataset.state ?? '';
    const postedHere = window.location.hash === `#${this.dataset.formId}`;
    // Tell the store's pixels about a new sign-up once, not again when the thank-you page is reloaded
    if (result === 'success' && postedHere && !storage.get(SUBSCRIBED_KEY)) {
      window.Shopify?.analytics?.publish?.('despina:newsletter_signup', { source: 'popup' });
    }
    if (result === 'success') storage.set(SUBSCRIBED_KEY, '1');

    if (result && postedHere) {
      this.open();
    } else {
      this.#timers.push(window.setTimeout(() => this.#showTeaser(), TEASER_DELAY_MS));
      this.#scheduleAutoOpen();
    }

    // Close on backdrop click (the dialog element itself is the backdrop area)
    dialog?.addEventListener(
      'click',
      (event) => {
        if (event.target === dialog) this.close();
      },
      { signal }
    );
    dialog?.addEventListener('close', () => this.#showTeaser(), { signal });

    // Theme editor: open while the section is selected
    document.addEventListener(
      'shopify:section:select',
      (event) => {
        if (/** @type {CustomEvent} */ (event).detail?.sectionId === this.dataset.sectionId) this.open();
      },
      { signal }
    );
    document.addEventListener(
      'shopify:section:deselect',
      (event) => {
        if (/** @type {CustomEvent} */ (event).detail?.sectionId === this.dataset.sectionId) this.close();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#timers.forEach((timer) => clearTimeout(timer));
  }

  open() {
    const { dialog, teaser } = this.refs;
    if (!dialog || dialog.open) return;
    if (teaser) teaser.hidden = true;
    dialog.showModal();
  }

  close() {
    this.refs.dialog?.close();
  }

  /** Hides the corner tab for a week */
  dismissTeaser() {
    const { teaser } = this.refs;
    if (teaser) teaser.hidden = true;
    storage.set(TEASER_HIDDEN_KEY, String(Date.now()));
  }

  /** @param {MouseEvent} event */
  async copyCode(event) {
    const button = event.target;
    const code = this.refs.code?.textContent?.trim();
    if (!code || !(button instanceof HTMLElement)) return;
    try {
      await navigator.clipboard.writeText(code);
      const original = button.textContent;
      button.textContent = button.dataset.copied ?? original;
      this.#timers.push(window.setTimeout(() => (button.textContent = original), 2000));
    } catch {
      // Clipboard can be blocked; the code stays visible to copy by hand
    }
  }

  #showTeaser() {
    const { teaser, dialog } = this.refs;
    if (!teaser || dialog?.open || storage.get(SUBSCRIBED_KEY)) return;
    const hiddenAt = Number(storage.get(TEASER_HIDDEN_KEY));
    if (hiddenAt && Date.now() - hiddenAt < TEASER_HIDE_DAYS * 864e5) return;
    teaser.hidden = false;
  }

  #scheduleAutoOpen() {
    const delay = Number(this.dataset.autoOpen);
    if (!delay || storage.get(SUBSCRIBED_KEY) || storage.get(AUTO_OPENED_KEY)) return;
    this.#timers.push(
      window.setTimeout(() => {
        storage.set(AUTO_OPENED_KEY, '1');
        this.open();
      }, delay * 1000)
    );
  }
}

if (!customElements.get('newsletter-popup')) {
  customElements.define('newsletter-popup', NewsletterPopupComponent);
}
